import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import React, { useEffect, useMemo, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { CardBody } from "../../../_metronic/_partials/controls";
import { getProductUnitTypes } from "../../../api/productUnitType";
import { alertError } from "../../../utils/logger";
import { ROLES } from "../../../utils/constants";
import FiltersCard from "../filters/Filter";
import Table from "../tables/table";
import CreateProductDialog from "./CreateProductDialog";

function getData(products) {
  let data = [];
  for (let i = 0; i < products.length; ++i) {
    const elem = {};
    const product = products[i];

    elem.referencia = product?.referencia;
    elem.descripcion = product?.descripcion;
    elem.marca = product?.marca?.nombre;
    elem.familia = product?.familia?.descripcion;
    elem.tipoProducto = product?.tipo_producto?.descripcion;
    elem.precio = product?.precio + " €";
    elem.id = product?.id;

    data = data.concat(elem);
  }
  return data;
}

const initialFilters = {
  familias: [],
  tipoProductos: [],
  marcas: [],
};

const ProductSelectTableDialog = (props) => {
  const {
    title,
    open,
    setOpen,
    data = [],
    initialSelectedProducts = [],
    productMarcas = [],
    productFamilies = [],
    productTypes = [],
    productUnitTypes: productUnitTypesFromParent,
    onSave,
		allowCreateProduct = true,
  } = props;

  const [selectedProducts, setSelectedProducts] = useState(
    initialSelectedProducts || []
  );

  const [filteredData, setFilteredData] = useState(data);
  const [collapsed, setCollapsed] = useState(true);
  const [filterOptions, setFilterOptions] = useState(initialFilters);

  const [openCreateProductDialog, setOpenCreateProductDialog] = useState(false);
  const [productUnitTypesLocal, setProductUnitTypesLocal] = useState([]);

  const loggedUser = useSelector(
    (store) => store.authentication?.user,
    shallowEqual
  );
  const isOfferManager = loggedUser?.role?.rango === ROLES.GESTOR_OFERTAS;

  const productUnitTypes = useMemo(() => {
    return (productUnitTypesFromParent && productUnitTypesFromParent.length
      ? productUnitTypesFromParent
      : productUnitTypesLocal) || [];
  }, [productUnitTypesFromParent, productUnitTypesLocal]);

  const sortedProductFamilies = useMemo(
    () =>
      [...productFamilies].sort((a, b) =>
        (a?.descripcion || "").localeCompare(b?.descripcion || "", "es", {
          sensitivity: "base",
        })
      ),
    [productFamilies]
  );

  useEffect(() => {
    setSelectedProducts(initialSelectedProducts);
    setFilteredData(data);
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const hasFromParent =
      Array.isArray(productUnitTypesFromParent) &&
      productUnitTypesFromParent.length > 0;

    if (hasFromParent) return;

    let cancelled = false;

    (async () => {
      try {
        const res = await getProductUnitTypes(loggedUser?.accessToken);
        if (cancelled) return;

        if (res?.status === 200) {
          setProductUnitTypesLocal(Array.isArray(res.data) ? res.data : []);
        } else {
          setProductUnitTypesLocal([]);
        }
      } catch (error) {
        if (cancelled) return;

        setProductUnitTypesLocal([]);
        alertError({
          error,
          customMessage: "No se han podido obtener los tipos de unidad.",
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, productUnitTypesFromParent, loggedUser?.accessToken]);

  const handleSearch = async () => {
    if (!data.length) return;
    setFilteredData(
      data.filter((item) => {
        let filter = true;
        if (filterOptions.marcas && filterOptions.marcas.length > 0)
          filter =
            filter &&
            filterOptions.marcas.some((x) => x.id === item?.marca?.id);
        if (filterOptions.familias && filterOptions.familias.length > 0)
          filter =
            filter &&
            filterOptions.familias?.some((x) => x.id === item?.familia?.id);
        if (
          filterOptions.tipoProductos &&
          filterOptions.tipoProductos.length > 0
        )
          filter =
            filter &&
            filterOptions.tipoProductos?.some(
              (x) => x.id === item?.tipo_producto?.id
            );
        if (filter) return item;
        return false;
      })
    );
  };

  const handleClearFilters = () => {
    setFilterOptions(initialFilters);
    setFilteredData(data);
  };

  const renderFiltersContent = () => {
    return (
      <>
        <Autocomplete
          className="w-100 mr-5"
          multiple
          id="autocomplete-marca"
          filterSelectedOptions
          options={productMarcas}
          getOptionLabel={(option) => option?.nombre}
          value={filterOptions.marcas || []}
          onChange={(event, selected) => {
            setFilterOptions({
              ...filterOptions,
              marcas: selected,
            });
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              margin="normal"
              variant="outlined"
              InputLabelProps={{
                shrink: true,
              }}
              label="Marcas"
            />
          )}
        />
        <div className="d-sm-flex w-100">
          <Autocomplete
            className="w-100 mr-5"
            multiple
            id="autocomplete-families"
            filterSelectedOptions
            options={sortedProductFamilies}
            getOptionLabel={(option) => option?.descripcion}
            value={filterOptions.familias || []}
            onChange={(event, selected) => {
              setFilterOptions({
                ...filterOptions,
                familias: selected,
              });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                margin="normal"
                variant="outlined"
                InputLabelProps={{
                  shrink: true,
                }}
                label="Familia"
              />
            )}
          />
          <Autocomplete
            className="w-100 mr-5"
            multiple
            id="autocomplete-tipos"
            filterSelectedOptions
            options={productTypes}
            getOptionLabel={(option) => option?.descripcion}
            value={filterOptions.tipoProductos || []}
            onChange={(event, selected) => {
              setFilterOptions({
                ...filterOptions,
                tipoProductos: selected,
              });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                margin="normal"
                variant="outlined"
                InputLabelProps={{
                  shrink: true,
                }}
                label="Tipo de Producto"
              />
            )}
          />
        </div>
      </>
    );
  };

  const columns = [
    {
      dataField: "referencia",
      text: "Referencia",
      sort: true,
      headerAlign: "left",
      align: "left",
      headerStyle: { width: "13%" },
      style: { width: "13%" },
    },
    {
      dataField: "descripcion",
      text: "Descripción",
      sort: true,
      headerAlign: "left",
      align: "left",
    },
    {
      dataField: "marca",
      text: "Marca",
      sort: true,
      headerAlign: "center",
      align: "center",
      headerStyle: { width: "8%" },
      style: { width: "8%" },
    },
    {
      dataField: "familia",
      text: "Familia",
      sort: true,
      headerAlign: "center",
      align: "center",
      headerStyle: { width: "10%" },
      style: { width: "10%" },
    },
    {
      dataField: "tipoProducto",
      text: "Tipo",
      sort: true,
      headerAlign: "center",
      align: "center",
      headerStyle: { width: "8%" },
      style: { width: "8%" },
    },
    {
      dataField: "precio",
      text: "Precio",
      sort: true,
      headerAlign: "center",
      align: "center",
      headerStyle: { width: "8%" },
      style: { width: "8%" },
    },
  ].filter((column) => !isOfferManager || column.dataField !== "precio");

  const selectRow = {
    mode: "checkbox",
    clickToSelect: true,
    selectColumnPosition: "right",
    headerColumnStyle: { width: "5%" },
    selectColumnStyle: { width: "5%" },
    style: { backgroundColor: "#c8e6c9" },
    selected: selectedProducts,
    onSelect: (row, isSelect, rowIndex, e) => {
      let products = [...selectedProducts];

      if (isSelect) products.push(row.id);
      else products = products.filter((id) => id !== row.id);

      setSelectedProducts(products);
    },
    onSelectAll: (isSelect, rows, e) => {
      let products = [...selectedProducts];

      if (isSelect) products = products.concat(rows.map((x) => x.id));
      else
        products = products.filter((id) => !rows.map((x) => x.id).includes(id));

      setSelectedProducts(products);
    },
  };

  const rowClasses = "cursor-pointer";

  return (
    <>
      <Dialog
        fullWidth={true}
        maxWidth="xl"
        open={open}
        onClose={() => {
          setOpen(false);
          setSelectedProducts([]);
        }}
        aria-labelledby="table-dialog"
        className="backdrop"
      >
        <DialogTitle id="table-dialog">
          <div className="d-flex justify-content-between">
            {title}
            {allowCreateProduct && <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                setOpenCreateProductDialog(true);
              }}
            >
              Añadir nuevo
            </button>}
          </div>
        </DialogTitle>
        <DialogContent>
          <FiltersCard
            className="px-0 mx-0"
            cardShadow={false}
            collapsable={false}
            filtersContent={renderFiltersContent}
            collapsed={collapsed}
            setCollapsed={setCollapsed}
            handleClearFilters={handleClearFilters}
            handleSearch={handleSearch}
          />
          <CardBody className="pt-0 px-0">
            {!filteredData || !filteredData.length ? (
              <p>{"No hay productos disponibles."}</p>
            ) : (
              <Table
                data={getData(filteredData)}
                columns={columns}
                selectRow={selectRow}
                rowClasses={rowClasses}
                persistPageInUrl={false}
              />
            )}
          </CardBody>
        </DialogContent>
        <DialogActions>
          <Button
            className="mr-2"
            onClick={() => {
              setOpen(false);
              setSelectedProducts([]);
            }}
            variant="outlined"
          >
            Cancelar
          </Button>
          <Button
            onClick={() => {
              setOpen(false);
              onSave(selectedProducts);
            }}
            variant="outlined"
            color="primary"
          >
            Guardar
          </Button>
          <br />
        </DialogActions>
      </Dialog>
			{allowCreateProduct && <CreateProductDialog
        title="Nuevo producto"
        open={openCreateProductDialog}
        setOpen={setOpenCreateProductDialog}
        productMarcas={productMarcas}
        productFamilies={productFamilies}
        productTypes={productTypes}
        productUnitTypes={productUnitTypes}
        onSave={(productId) => {
          setOpen(false);
          onSave(productId);
        }}
			/>}
    </>
  );
};
export default ProductSelectTableDialog;

